import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { can, type Capability, type DemoRole, type DemoState } from "./model";
import { createSeed } from "./seed";
type Notice = { id: number; text: string; error?: boolean };
type Context = {
  state: DemoState;
  role: DemoRole;
  setRole: (r: DemoRole) => void;
  notices: Notice[];
  dismiss: (id: number) => void;
  notify: (text: string, error?: boolean) => void;
  pending: string | null;
  allowed: (c?: Capability) => boolean;
  mutate: (
    key: string,
    success: string,
    update: (draft: DemoState) => void,
    capability?: Capability,
  ) => Promise<boolean>;
  reset: () => void;
};
const DemoContext = createContext<Context | null>(null);
export function DemoProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(createSeed);
  const [role, setRole] = useState<DemoRole>("owner");
  const [notices, setNotices] = useState<Notice[]>([]);
  const [pending, setPending] = useState<string | null>(null);
  const busy = useRef(false);
  const notify = useCallback((text: string, error = false) => {
    const id = Date.now() + Math.random();
    setNotices((n) => [...n.slice(-2), { id, text, error }]);
    window.setTimeout(
      () => setNotices((n) => n.filter((x) => x.id !== id)),
      5500,
    );
  }, []);
  const mutate: Context["mutate"] = async (
    key,
    success,
    update,
    capability = "operate",
  ) => {
    if (!can(role, capability)) {
      notify("Your preview role cannot make this change.", true);
      return false;
    }
    if (busy.current) return false;
    busy.current = true;
    setPending(key);
    try {
      await new Promise((r) => setTimeout(r, 320));
      setState((current) => {
        const draft = structuredClone(current);
        update(draft);
        return draft;
      });
      notify(`${success} · demo only`);
      return true;
    } finally {
      busy.current = false;
      setPending(null);
    }
  };
  return (
    <DemoContext.Provider
      value={{
        state,
        role,
        setRole,
        notices,
        notify,
        pending,
        mutate,
        allowed: (c = "operate") => can(role, c),
        dismiss: (id) => setNotices((n) => n.filter((x) => x.id !== id)),
        reset: () => {
          if (busy.current) {
            notify("Wait for the current demo change to finish.", true);
            return;
          }
          setState(createSeed());
          notify("Demo data reset");
        },
      }}
    >
      {children}
    </DemoContext.Provider>
  );
}
export function useDemo() {
  const value = useContext(DemoContext);
  if (!value) throw new Error("DemoProvider is required");
  return value;
}
